import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { HubDataTable, type HubDataTableColumn } from "@/components/hub/HubDataTable";
import { WorkOrderDetailLink } from "@/components/hub/HubEntityDetailLink";
import { MrpSkuDetailLink } from "@/components/MrpSkuDetailLink";
import { MrpSkuCatalogHoverHintBody } from "@/components/mrp/MrpSkuCatalogHoverHintBody";
import { PurchaseOrderHubLinkWithMegaventory } from "@/components/mrp/MegaventoryPurchaseOrderLinks";
import { MrpSkuWithUnitLabel } from "@/components/mrp/MrpSkuWithUnitLabel";
import { Button } from "@/components/ui/Button";
import { Card, CardBody } from "@/components/ui/Card";
import { HoverHintDefinitionGrid } from "@/components/ui/HoverHintDefinitionGrid";
import { Input, Label } from "@/components/ui/Input";
import { hubModalOverlayProps } from "@/components/ui/HubModalOverlay";
import { HoverHintContent, ReadOnlyHoverLayer } from "@/components/ui/title-help";
import { ApiError, apiFetch } from "@/lib/api";
import { type MrpSkuHoverHintFields } from "@/lib/mrp";
import { useManufacturingScopeHubWrite } from "@/lib/useHubModuleWrite";

import { WorkOrderDetailWorkspace } from "./workOrderDetailWorkspace";

export interface CmPoReceivingLine {
  purchase_order_item_id: number;
  cm_purchase_order_id: number;
  public_purchase_order_id: number;
  mv_purchase_order_id: number;
  mv_purchase_order_number: string | null;
  supplier_name: string | null;
  location_name: string | null;
  po_line_sku: string;
  po_line_description: string | null;
  quantity_ordered: number;
  hub_quantity_received: number;
  vendor_invoice_quantity: number;
  mv_status: string | null;
  work_orders: {
    id: number;
    public_build_order_id: number;
    sku: string;
    quantity: number;
    mv_work_order_number: string | null;
    wofg_posted_quantity: number;
    transfer_quantity: number;
    stays_at_build_site: boolean;
  }[];
}

function formatNumber(value: number): string {
  return value.toLocaleString("en-US", { maximumFractionDigits: 2 });
}

function wofgPostedQuantity(line: CmPoReceivingLine): number {
  return line.work_orders.reduce((sum, workOrder) => sum + workOrder.wofg_posted_quantity, 0);
}

function transferredQuantityLabel(line: CmPoReceivingLine): string {
  const expectsTransfer = line.work_orders.some((workOrder) => !workOrder.stays_at_build_site);
  if (!expectsTransfer) return "N/A";
  const quantity = line.work_orders
    .filter((workOrder) => !workOrder.stays_at_build_site)
    .reduce((sum, workOrder) => sum + workOrder.transfer_quantity, 0);
  return formatNumber(quantity);
}

function cmPoReceivingPeekBody(line: CmPoReceivingLine) {
  return (
    <HoverHintDefinitionGrid
      rows={[
        { label: "Supplier", value: line.supplier_name ?? "—" },
        { label: "Status", value: line.mv_status ?? "—" },
        { label: "Location", value: line.location_name ?? "—" },
        { label: "Line SKU", value: <span className="font-mono">{line.po_line_sku}</span> },
      ]}
    />
  );
}

export function CmPoReceivingPage() {
  const hubWritesAllowed = useManufacturingScopeHubWrite();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get("po") ?? "");
  const [managedBuildOrderId, setManagedBuildOrderId] = useState<number | null>(null);
  const [manageMenuItemId, setManageMenuItemId] = useState<number | null>(null);

  const listQuery = useQuery({
    queryKey: ["manufacturing", "cm-po", "receiving"],
    queryFn: () => apiFetch<{ rows: CmPoReceivingLine[] }>("/api/manufacturing/cm-po/receiving"),
    staleTime: 30_000,
  });

  const rows = listQuery.data?.rows ?? [];

  const skuList = useMemo(() => {
    const skus = new Set<string>();
    for (const row of rows) {
      const lineSku = row.po_line_sku.trim();
      if (lineSku.length > 0) skus.add(lineSku);
      for (const workOrder of row.work_orders) {
        const workOrderSku = workOrder.sku.trim();
        if (workOrderSku.length > 0) skus.add(workOrderSku);
      }
    }
    return [...skus];
  }, [rows]);

  const skuHintsQuery = useQuery({
    queryKey: ["mrp", "sku-hover-hints", "cm-po-receiving", skuList],
    queryFn: () =>
      apiFetch<{ sku_hover_hints: Record<string, MrpSkuHoverHintFields> }>("/api/mrp/sku-hover-hints", {
        query: { cpns: skuList.join(",") },
      }),
    enabled: skuList.length > 0,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  const skuHoverHints = skuHintsQuery.data?.sku_hover_hints;

  const columns = useMemo<HubDataTableColumn<CmPoReceivingLine>[]>(() => [
    {
      id: "po",
      header: "PO",
      sortValue: (line) => line.mv_purchase_order_number ?? "",
      cell: (line) => (
        <HoverHintContent hoverHint={cmPoReceivingPeekBody(line)} block cursor="default">
          <PurchaseOrderHubLinkWithMegaventory
            purchaseOrderId={line.public_purchase_order_id}
            poNumber={line.mv_purchase_order_number}
            purchaseOrderMvId={line.mv_purchase_order_id}
          />
        </HoverHintContent>
      ),
    },
    {
      id: "supplier",
      header: "Supplier",
      sortValue: (line) => line.supplier_name ?? "",
      cell: (line) => line.supplier_name ?? "—",
    },
    {
      id: "sku",
      header: "PO line SKU",
      sortValue: (line) => line.po_line_sku,
      cell: (line) => {
        const lineSkuHint = skuHoverHints?.[line.po_line_sku.trim()];
        return (
          <>
            <HoverHintContent
              hoverHint={
                <MrpSkuCatalogHoverHintBody
                  sku={line.po_line_sku}
                  hint={lineSkuHint}
                  loading={skuHintsQuery.isPending}
                />
              }
              block
              cursor="default"
              className="min-w-0"
            >
              <MrpSkuDetailLink
                sku={line.po_line_sku}
                className="block font-mono text-xs font-medium text-brand-700 hover:text-brand-900"
              >
                <MrpSkuWithUnitLabel sku={line.po_line_sku} unit={lineSkuHint?.mv_unit} />
              </MrpSkuDetailLink>
            </HoverHintContent>
            {line.po_line_description ? (
              <span className="mt-0.5 block text-xs leading-snug text-slate-500">
                {line.po_line_description}
              </span>
            ) : null}
          </>
        );
      },
    },
    {
      id: "work_order",
      header: "Work order",
      overflow: "nowrap",
      sortValue: (line) => line.work_orders.map((workOrder) => workOrder.mv_work_order_number ?? workOrder.sku).join(", "),
      cell: (line) => (
        <div className="flex w-max flex-col gap-1">
          {line.work_orders.map((workOrder) => (
            <WorkOrderDetailLink
              key={workOrder.id}
              buildOrderId={workOrder.public_build_order_id}
              workOrderNumber={workOrder.mv_work_order_number}
              className="inline-flex w-max flex-col items-start gap-0.5 rounded-md border border-slate-200 bg-slate-100 px-1.5 py-0.5 text-[11px] font-normal text-slate-700 no-underline hover:bg-slate-200 hover:text-slate-900"
            >
              {workOrder.mv_work_order_number ? (
                <span className="text-slate-500">{workOrder.mv_work_order_number}</span>
              ) : null}
              <span className="whitespace-nowrap font-mono">{workOrder.sku}</span>
            </WorkOrderDetailLink>
          ))}
          {line.work_orders.length === 0 ? <span className="text-slate-400">—</span> : null}
        </div>
      ),
    },
    {
      id: "ordered",
      header: "Ordered",
      align: "right",
      sortValue: (line) => line.quantity_ordered,
      cell: (line) => formatNumber(line.quantity_ordered),
    },
    {
      id: "wofg",
      header: "WOFG",
      align: "right",
      sortValue: (line) => wofgPostedQuantity(line),
      cell: (line) => formatNumber(wofgPostedQuantity(line)),
    },
    {
      id: "transferred",
      header: "Transferred",
      align: "right",
      sortValue: (line) => {
        const expectsTransfer = line.work_orders.some((workOrder) => !workOrder.stays_at_build_site);
        if (!expectsTransfer) return null;
        return line.work_orders
          .filter((workOrder) => !workOrder.stays_at_build_site)
          .reduce((sum, workOrder) => sum + workOrder.transfer_quantity, 0);
      },
      cell: (line) => transferredQuantityLabel(line),
    },
    {
      id: "invoiced",
      header: "Invoiced",
      align: "right",
      sortValue: (line) => line.vendor_invoice_quantity,
      cell: (line) => formatNumber(line.vendor_invoice_quantity),
    },
    {
      id: "received",
      header: "Received",
      align: "right",
      sortValue: (line) => line.hub_quantity_received,
      cell: (line) => formatNumber(line.hub_quantity_received),
    },
    {
      id: "manage",
      header: "Manage",
      cellClassName: "relative",
      sortValue: (line) => line.work_orders.length,
      cell: (line) => {
        if (line.work_orders.length === 0) return null;
        if (line.work_orders.length === 1) {
          return (
            <Button
              type="button"
              size="sm"
              variant="secondary"
              className="whitespace-nowrap"
              onClick={() => setManagedBuildOrderId(line.work_orders[0].public_build_order_id)}
            >
              Manage work order
            </Button>
          );
        }
        return (
          <>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              className="whitespace-nowrap"
              aria-expanded={manageMenuItemId === line.purchase_order_item_id}
              onClick={() =>
                setManageMenuItemId((current) =>
                  current === line.purchase_order_item_id ? null : line.purchase_order_item_id,
                )
              }
            >
              Manage work order
            </Button>
            {manageMenuItemId === line.purchase_order_item_id ? (
              <ul className="absolute right-3 z-20 mt-1 min-w-full rounded-md border border-slate-200 bg-white py-1 shadow-md">
                {line.work_orders.map((workOrder) => (
                  <li key={workOrder.id}>
                    <button
                      type="button"
                      className="block w-full px-3 py-1.5 text-left text-xs text-slate-800 hover:bg-slate-50"
                      onClick={() => {
                        setManageMenuItemId(null);
                        setManagedBuildOrderId(workOrder.public_build_order_id);
                      }}
                    >
                      {workOrder.mv_work_order_number ?? workOrder.sku}
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
          </>
        );
      },
    },
  ], [manageMenuItemId, skuHintsQuery.isPending, skuHoverHints]);

  const filteredRows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((row) => {
      if ((row.mv_purchase_order_number ?? "").toLowerCase().includes(needle)) return true;
      if ((row.supplier_name ?? "").toLowerCase().includes(needle)) return true;
      if (row.po_line_sku.toLowerCase().includes(needle)) return true;
      if (row.work_orders.some((workOrder) =>
        workOrder.sku.toLowerCase().includes(needle)
        || (workOrder.mv_work_order_number ?? "").toLowerCase().includes(needle)
      )) return true;
      return false;
    });
  }, [rows, search]);

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-xl font-semibold text-slate-900">Receiving</h1>
        <p className="text-sm text-slate-500">
          Compare the purchase order, finished goods, vendor invoice, and receipt. Manage the work
          order here to check the bill of materials and post finished goods.
        </p>
      </header>

      <Card>
        <CardBody className="space-y-3">
          <div className="max-w-md">
            <Label htmlFor="cm-po-receiving-search">Search open lines</Label>
            <Input
              id="cm-po-receiving-search"
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="PO number, supplier, SKU…"
              autoComplete="off"
            />
          </div>
          <p className="text-xs text-slate-500">
            Showing {filteredRows.length} of {rows.length} line
            {rows.length === 1 ? "" : "s"}.
          </p>
        </CardBody>
      </Card>

      <ReadOnlyHoverLayer
        active={!hubWritesAllowed}
        hoverHint="Your role has read-only access to the Manufacturing hub module."
        layout="block"
      >
        <HubDataTable
          columns={columns}
          rows={filteredRows}
          rowKey={(line) => line.purchase_order_item_id}
          loading={listQuery.isPending}
          error={
            listQuery.isError ? (
              <div className="text-center text-red-600">
                <p>Failed to load CM PO receiving lines.</p>
                {listQuery.error instanceof ApiError ? (
                  <p className="mt-1 font-mono text-xs text-red-700">
                    HTTP {listQuery.error.status}: {listQuery.error.message}
                  </p>
                ) : listQuery.error instanceof Error ? (
                  <p className="mt-1 font-mono text-xs text-red-700">{listQuery.error.message}</p>
                ) : null}
              </div>
            ) : null
          }
          empty="No CM PO lines to show."
          isFiltered={search.trim().length > 0 && rows.length > 0}
          filteredEmpty="No lines match this search."
        />
      </ReadOnlyHoverLayer>

      {managedBuildOrderId != null
        ? createPortal(
            <div
              {...hubModalOverlayProps(false)}
              role="dialog"
              aria-modal="true"
              aria-label="Manage work order"
            >
              <div className="my-6 w-full max-w-5xl">
                <WorkOrderDetailWorkspace
                  buildOrderId={managedBuildOrderId}
                  receiveFromCmPo
                  onClose={() => setManagedBuildOrderId(null)}
                  onMutated={() => {
                    void queryClient.invalidateQueries({ queryKey: ["manufacturing", "cm-po", "receiving"] });
                  }}
                />
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
